// components/NotificationsPanel.jsx
import { useEffect, useState } from "react";
import {
  getFirestore,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  startAfter,
  onSnapshot,
  updateDoc,
  doc,
} from "firebase/firestore";
import { useAuth } from "../authContext";
import {
  Box,
  Paper,
  Stack,
  Chip,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  IconButton,
} from "@mui/material";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { useNavigate } from "react-router-dom";

// "DD MMM, HH:mm" with native Intl
function formatShort(dtLike) {
  const d =
    dtLike && typeof dtLike.toDate === "function"
      ? dtLike.toDate()
      : new Date(dtLike || 0);
  if (Number.isNaN(+d)) return "";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Pagination actions (Prev/Next) with ability to disable Next when no more docs
function PaginationActions({ page, onPageChange, disableNext }) {
  return (
    <Stack direction="row" alignItems="center">
      <IconButton onClick={(e) => onPageChange(e, page - 1)} disabled={page === 0}>
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton onClick={(e) => onPageChange(e, page + 1)} disabled={disableNext}>
        <KeyboardArrowRight />
      </IconButton>
    </Stack>
  );
}

/**
 * variant:
 *  - "panel" (default): compact list (drawer)
 *  - "page": full-width table with pagination
 */
export default function NotificationsPanel({ open, onClose, variant = "panel" }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // --- pagination state (page variant) ---
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [cursors, setCursors] = useState([]); // last visible doc per page
  const [canNext, setCanNext] = useState(false);
  const navigate = useNavigate();

  // ===================== LISTEN (panel) =====================
  const shouldListen = (variant === "page" && !!user) || (open && !!user);

  // Drawer/panel keeps realtime list (no pagination)
  useEffect(() => {
    if (!shouldListen || variant !== "panel") return;
    const db = getFirestore();
    const q = query(
      collection(db, "users", user.uid, "notifications"),
      orderBy("createdAt", "desc"),
      limit(30)
    );
    return onSnapshot(q, (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setItems(arr);
      setUnreadCount(arr.filter((i) => !i.read).length);
    });
  }, [shouldListen, user, variant]);

  // ===================== PAGINATION (page) =====================
  async function loadPage(targetPage, rpp) {
    if (!user) return;
    const db = getFirestore();

    // base query
    let qBase = query(
      collection(db, "users", user.uid, "notifications"),
      orderBy("createdAt", "desc")
    );

    // apply cursor if not first page
    if (targetPage > 0 && cursors[targetPage - 1]) {
      qBase = query(qBase, startAfter(cursors[targetPage - 1]));
    }

    // ask for one extra to know if "next" exists
    const snap = await getDocs(query(qBase, limit(rpp + 1)));
    const docs = snap.docs;
    const thereIsNext = docs.length > rpp;

    // show only the current page rows
    const pageDocs = thereIsNext ? docs.slice(0, rpp) : docs;

    // keep last visible as the cursor for this page
    const lastVisible = pageDocs[pageDocs.length - 1] || null;
    const nextCursors = [...cursors];
    nextCursors[targetPage] = lastVisible;
    setCursors(nextCursors);

    const arr = pageDocs.map((d) => ({ id: d.id, ...d.data() }));
    setItems(arr);
    setUnreadCount(arr.filter((i) => !i.read).length);
    setCanNext(!thereIsNext); // disable "next" when no more rows
    setPage(targetPage);
  }

  // initial load & refresh on rowsPerPage change (page variant)
  useEffect(() => {
    if (variant !== "page" || !user) return;
    setPage(0);
    setCursors([]);
    loadPage(0, rowsPerPage);
    
  }, [variant, user, rowsPerPage]);

  const handleChangePage = (_e, newPage) => {
    if (newPage < 0) return;
    loadPage(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (e) => {
    const rpp = parseInt(e.target.value, 10);
    setRowsPerPage(rpp);
  };

  // ===================== Actions =====================
  const markRead = async (id) => {
    const db = getFirestore();
    await updateDoc(doc(db, "users", user.uid, "notifications", id), { read: true });
    // optimistically update current view
    setItems((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
    setUnreadCount((c) => Math.max(0, c - 1));
  };

  const onItem = async (n) => {
    await markRead(n.id);
    if (n.taskId) navigate(`/tasks?focus=${n.taskId}`);
  };

  const rootSx =
  variant === "page"
    ? { 
        width: "100%", 
        maxWidth: 1200, 
        mx: "auto", 
        mt: 2,          // 👈 add this for top spacing
        px: { xs: 2, md: 3 } 
      }
    : { width: 360, p: 1.5 };
    

  // ===================== PAGE (table) =====================
  if (variant === "page") {
    return (
      <Box sx={rootSx}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h5">Notifications</Typography>
          <Chip size="small" label={`${unreadCount} unread`} variant="outlined" />
        </Stack>

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
          {items.length === 0 ? (
            <Box sx={{ px: 2, py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                You’re all caught up. No notifications yet.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="medium" aria-label="notifications">
                  <TableHead>
                    <TableRow>
                      <TableCell width={120}>Type</TableCell>
                      <TableCell>Title / Details</TableCell>
                      <TableCell width={220}>By</TableCell>
                      <TableCell width={160}>When</TableCell>
                      <TableCell width={120}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((n) => {
                      const typeLabel =
                        n.type === "task_assigned"
                          ? "Task"
                          : n.type === "csv_upload"
                          ? "CSV Upload"
                          : n.type || "Info";
                      return (
                        <TableRow
                          key={n.id}
                          hover
                          onClick={() => onItem(n)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>
                            <Chip
                              size="small"
                              label={typeLabel}
                              color={
                                n.type === "task_assigned"
                                  ? "success"
                                  : n.type === "csv_upload"
                                  ? "info"
                                  : "default"
                              }
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" sx={{ fontWeight: n.read ? 600 : 800 }}>
                              {n.title || n.type}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {n.body || ""}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {n.byUserName || "—"}
                            </Typography>
                            {n.byUserId ? (
                              <Typography variant="caption" color="text.secondary">
                                {n.byUserId}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{formatShort(n.createdAt)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={n.read ? "Read" : "New"}
                              color={n.read ? "default" : "success"}
                              variant={n.read ? "outlined" : "filled"}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination bar (unknown total) */}
              <TablePagination
                component="div"
                count={-1} // unknown total
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage="Rows"
                ActionsComponent={(subprops) => (
                  <PaginationActions
                    page={subprops.page}
                    onPageChange={subprops.onPageChange}
                    disableNext={canNext} // disable when there is no next page
                  />
                )}
              />
            </>
          )}
        </Paper>
      </Box>
    );
  }

  // ===================== PANEL (drawer list) =====================
  return (
    <Box sx={rootSx}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6">Notifications</Typography>
        <Chip size="small" label={`${unreadCount} unread`} variant="outlined" />
      </Stack>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <List disablePadding>
          {items.map((n, idx) => (
            <Box component="li" key={n.id}>
              <ListItemButton onClick={() => onItem(n)} sx={{ px: 2, py: 1.25 }}>
                <ListItemText
                  primary={n.title || n.type}
                  secondary={`${n.body || ""}  —  ${formatShort(n.createdAt)}`}
                  primaryTypographyProps={{ fontWeight: n.read ? 500 : 700 }}
                />
              </ListItemButton>
              {idx < items.length - 1 && <Divider component="div" />}
            </Box>
          ))}
          {items.length === 0 && (
            <Box sx={{ px: 2, py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                You’re all caught up. No notifications yet.
              </Typography>
            </Box>
          )}
        </List>
      </Paper>

      {variant === "panel" && (
        <Button onClick={onClose} fullWidth sx={{ mt: 1 }}>
          Close
        </Button>
      )}
    </Box>
  );
}
